/**
 * `validate/email` — RFC-5321/5322 simplified regex.
 *
 * We deliberately pick the WHATWG / HTML-living-standard "valid
 * e-mail address" production rather than the full RFC 5322 grammar.
 * Why:
 *
 *   - RFC 5322 allows quoted locals (`"foo@bar"@example.com`),
 *     comments inside the address, and CFWS — none of which any
 *     real mail server in 2025 accepts. Implementing full RFC 5322
 *     would mean accepting addresses every modern mail provider
 *     would refuse.
 *
 *   - The simplified production matches what the browser's
 *     `<input type=email>` accepts AND what the Mailgun /
 *     Postmark / SES validators accept. That's what users actually
 *     want.
 *
 * Beyond the hard pass/fail, we emit warnings:
 *
 *   - too-long local part (> 64 octets — RFC 5321 §4.5.3.1.1)
 *   - too-long domain    (> 253 octets — RFC 5321 §4.5.3.1.2)
 *   - leading / trailing dot in local part (illegal but easy to typo)
 *   - consecutive dots
 *   - non-ASCII characters in domain (IDN — needs Punycode
 *     encoding for SMTP transmission; SMTPUTF8 is not yet
 *     universal)
 *
 * We intentionally do NOT touch DNS / SMTP — even with CORS, the
 * browser cannot reliably probe MX records, and any "this address
 * is reachable" claim from the page would be wrong.
 */

export interface ValidationIssue {
  severity: 'error' | 'warn';
  code: string;
  message: string;
}

export interface EmailValidation {
  ok: boolean;
  parts?: { local: string; domain: string };
  issues: ValidationIssue[];
}

const LOCAL_MAX = 64;
const DOMAIN_MAX = 253;
const TOTAL_MAX = 254;

// HTML living standard "valid e-mail address" regex.
// Source: https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const HTML_EMAIL =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const NON_ASCII = /[^\x00-\x7F]/;

export function validateEmail(input: string): EmailValidation {
  const issues: ValidationIssue[] = [];
  const trimmed = input.trim();

  if (!trimmed) {
    return {
      ok: false,
      issues: [{ severity: 'error', code: 'empty', message: '邮箱为空' }],
    };
  }
  if (trimmed !== input) {
    issues.push({
      severity: 'warn',
      code: 'whitespace',
      message: '存在前后空白字符（已自动忽略）',
    });
  }

  // Quick structural split — `@` MUST appear, exactly once we trust
  // for parsing purposes; the regex enforces no @ inside local.
  const at = trimmed.lastIndexOf('@');
  if (at === -1) {
    issues.push({
      severity: 'error',
      code: 'no-at',
      message: '缺少 @ 符号',
    });
    return { ok: false, issues };
  }
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);

  if (!local) {
    issues.push({
      severity: 'error',
      code: 'empty-local',
      message: 'local 部分为空',
    });
  }
  if (!domain) {
    issues.push({
      severity: 'error',
      code: 'empty-domain',
      message: 'domain 部分为空',
    });
  }

  // Reject extra @ in local (the regex would also catch this — we
  // surface a clearer message).
  if ((trimmed.match(/@/g) ?? []).length > 1) {
    issues.push({
      severity: 'error',
      code: 'multiple-at',
      message: '存在多个 @ 符号',
    });
  }

  if (local.startsWith('.') || local.endsWith('.')) {
    issues.push({
      severity: 'error',
      code: 'local-dot-edge',
      message: 'local 部分不能以点号开头或结尾',
    });
  }
  if (local.includes('..')) {
    issues.push({
      severity: 'error',
      code: 'local-double-dot',
      message: 'local 部分不能包含连续的点号',
    });
  }

  if (local.length > LOCAL_MAX) {
    issues.push({
      severity: 'error',
      code: 'local-too-long',
      message: `local 部分超过 ${LOCAL_MAX} 字符（当前 ${local.length}）`,
    });
  }
  if (domain.length > DOMAIN_MAX) {
    issues.push({
      severity: 'error',
      code: 'domain-too-long',
      message: `domain 部分超过 ${DOMAIN_MAX} 字符（当前 ${domain.length}）`,
    });
  }
  if (trimmed.length > TOTAL_MAX) {
    issues.push({
      severity: 'warn',
      code: 'total-too-long',
      message: `整体超过 ${TOTAL_MAX} 字符（RFC 5321 限制）`,
    });
  }

  if (NON_ASCII.test(domain)) {
    issues.push({
      severity: 'warn',
      code: 'idn-domain',
      message: 'domain 包含非 ASCII 字符（IDN）；发送时需要 Punycode 编码',
    });
  }

  // Final structural pass — only run the regex if we don't already
  // have a clear structural error, so messages come out in a
  // sensible order.
  const hasStructuralError = issues.some(
    (i) => i.severity === 'error' && i.code !== 'whitespace',
  );
  if (!hasStructuralError && !HTML_EMAIL.test(trimmed)) {
    issues.push({
      severity: 'error',
      code: 'syntax',
      message: '不符合 RFC 5321/5322 简化语法',
    });
  }

  const ok = !issues.some((i) => i.severity === 'error');
  return ok
    ? { ok, parts: { local, domain }, issues }
    : { ok, issues };
}
